import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass, ShieldCheck, Sparkle, ChartLineUp, Lock, Scales, ArrowRight,
  CheckCircle, Storefront, Robot, PenNib, Browsers, Target, MapPin, Code,
  ShareNetwork, LinkSimple, EnvelopeSimple, GearSix, PaintBrush, SealCheck,
} from "@phosphor-icons/react";

const ICONS = {
  MagnifyingGlass, MapPin, Storefront, LinkSimple, PenNib, Browsers, Code, Target,
  ShareNetwork, EnvelopeSimple, GearSix, Robot, PaintBrush, ChartLineUp,
};

export default function Home() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [experts, setExperts] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/services?limit=8&sort=most_orders").then(({ data }) => setFeatured(data.items));
    api.get("/sellers?limit=4&tier=certified").then(({ data }) => setExperts(data));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    nav(`/browse${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`);
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 mv-hero-grid opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-mv-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-mv-accent/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold uppercase tracking-[0.18em] mb-6"
                data-testid="hero-eyebrow">
                <Sparkle size={14} weight="duotone" /> Vetted Marketplace · 14 Service Categories
              </div>

              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-mv-secondary"
                data-testid="hero-headline">
                Find trusted digital experts
                <span className="block text-mv-primary">to grow your business.</span>
              </h1>

              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                MarketVyne connects businesses with vetted SEO, web design, content, and marketing professionals —
                with transparent pricing, clear deliverables, and fair buyer/seller protection.
              </p>

              <form onSubmit={onSearch} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
                data-testid="hero-search-form">
                <div className="flex-1 relative">
                  <MagnifyingGlass size={20} weight="bold"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    data-testid="hero-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder='e.g. "technical SEO audit" or "Klaviyo flows"'
                    className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 bg-white text-base focus:border-mv-primary focus:ring-2 focus:ring-mv-primary/20"
                  />
                </div>
                <Button type="submit" className="bg-mv-primary hover:bg-mv-primary-light text-white font-bold px-7 py-4 h-auto rounded-xl"
                  data-testid="hero-search-btn">
                  Find services <ArrowRight size={18} weight="bold" className="ml-2" />
                </Button>
              </form>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link to="/become-a-seller" data-testid="hero-seller-btn">
                  <Button variant="outline" className="font-bold border-2 border-mv-secondary text-mv-secondary hover:bg-mv-secondary hover:text-white rounded-xl">
                    Become a seller
                  </Button>
                </Link>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <ShieldCheck size={14} weight="duotone" className="text-mv-primary" />
                  Escrow-style protection · No upfront seller fees
                </span>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4 sm:gap-6 text-xs text-gray-500">
                <div className="flex items-center gap-2"><CheckCircle weight="duotone" className="text-mv-primary" /> Vetted sellers</div>
                <div className="flex items-center gap-2"><CheckCircle weight="duotone" className="text-mv-primary" /> Clear deliverables</div>
                <div className="flex items-center gap-2"><CheckCircle weight="duotone" className="text-mv-primary" /> Risk disclosure</div>
              </div>
            </div>

            <div className="lg:col-span-5 min-w-0 relative mt-6 lg:mt-0" data-testid="hero-visual">
              {/* MAIN PREVIEW CARD — frames the marketplace discovery flow.
                  Every number that appears is either:
                    (a) real, pulled from the live API (cats.length, real services), or
                    (b) clearly labeled as an example / product capability (not a metric).
                  No invented ROI, buyer count, order count, or rating volume. */}
              <div className="relative rounded-3xl bg-white border border-gray-200 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.18)] overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-mv-primary" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-mv-secondary">
                      Marketplace preview
                    </span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                    {featured.length > 0 ? "Live listings" : "Example matches"}
                  </span>
                </div>

                {/* Capability promise (replaces the fabricated ROI stat) */}
                <div className="px-5 pt-5 pb-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                    How MarketVyne matches you
                  </div>
                  <div className="font-display font-black text-2xl text-mv-secondary leading-tight mt-1.5">
                    Vetted experts.<br/>
                    <span className="text-mv-primary">Clear deliverables.</span>
                  </div>
                  <div className="text-[12px] text-gray-500 mt-2 leading-relaxed">
                    Structured packages, transparent pricing, and risk disclosure on every listing.
                  </div>
                </div>

                {/* Capability row — product features, not metrics */}
                <div className="px-5 pb-4 grid grid-cols-3 gap-2.5">
                  {[
                    { label: "Vetted profiles", icon: SealCheck },
                    { label: "Escrow checkout", icon: Lock },
                    { label: "Risk disclosure", icon: ShieldCheck },
                  ].map((c) => (
                    <div key={c.label} className="rounded-xl bg-gray-50 px-2.5 py-2.5">
                      <c.icon size={14} weight="duotone" className="text-mv-primary mb-1" />
                      <div className="text-[10px] font-bold text-mv-secondary leading-tight">
                        {c.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Service preview rows — real listings when available, otherwise
                    clearly-labeled illustrative examples. */}
                <div className="px-5 pb-5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      {featured.length > 0 ? "Live listings" : "Example matches"}
                    </div>
                    {featured.length === 0 && (
                      <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
                        Illustrative
                      </div>
                    )}
                  </div>

                  {(featured.length > 0
                    ? featured.slice(0, 2).map((s, idx) => ({
                        id: s.id,
                        title: s.title,
                        price: s.starting_price,
                        risk: s.seo_risk,
                        initial: (s.title || "S").charAt(0).toUpperCase(),
                        bg: idx === 0 ? "bg-mv-primary" : "bg-mv-secondary",
                      }))
                    : [
                        { id: "ex1", title: "Technical SEO audit", price: 149,
                          risk: "low", initial: "T", bg: "bg-mv-primary" },
                        { id: "ex2", title: "Conversion-focused landing page", price: 499,
                          risk: null, initial: "C", bg: "bg-mv-secondary" },
                      ]
                  ).map((s) => (
                    <div key={s.id}
                         className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100">
                      <div className={`w-9 h-9 rounded-xl ${s.bg} text-white font-display font-black text-sm flex items-center justify-center shrink-0`}>
                        {s.initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-[13px] text-mv-secondary truncate">
                          {s.title}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-0.5">
                          <span className="flex items-center gap-1 text-mv-primary font-semibold">
                            <SealCheck size={11} weight="fill" /> Vetted seller
                          </span>
                          {s.risk && (
                            <>
                              <span className="w-1 h-1 rounded-full bg-gray-300" />
                              <span className="text-mv-primary font-semibold capitalize">
                                {s.risk} risk
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-black text-sm text-mv-secondary">
                          ${s.price}
                        </div>
                        <div className="text-[9px] uppercase tracking-wider text-gray-400">
                          starting
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating: Buyer/seller protection (capability claim, not a metric) */}
              <div className="hidden sm:flex absolute -top-5 -right-4 lg:-right-6 bg-mv-secondary text-white rounded-2xl shadow-[0_15px_40px_-10px_rgba(15,23,42,0.45)] px-4 py-3 items-center gap-3"
                   data-testid="hero-trust-badge">
                <div className="w-10 h-10 rounded-xl bg-mv-accent/20 flex items-center justify-center">
                  <ShieldCheck size={20} weight="duotone" className="text-mv-accent" />
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-mv-accent">
                    Buyer + seller protection
                  </div>
                  <div className="font-display font-bold text-sm leading-tight mt-0.5">
                    Escrow-style<br/>checkout
                  </div>
                </div>
              </div>

              {/* Floating: real category count (cats.length is the live DB count) */}
              <div className="hidden sm:flex absolute -bottom-5 -left-4 lg:-left-6 bg-white rounded-2xl border border-gray-200 shadow-[0_15px_40px_-10px_rgba(15,23,42,0.18)] px-4 py-3 items-center gap-3"
                   data-testid="hero-categories-badge">
                <div className="w-10 h-10 rounded-xl bg-mv-primary/10 flex items-center justify-center">
                  <Storefront size={20} weight="duotone" className="text-mv-primary" />
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-mv-primary">
                    Service categories
                  </div>
                  <div className="font-display font-black text-xl text-mv-secondary leading-none mt-0.5">
                    {cats.length > 0 ? cats.length : 14}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">SEO, web, content, ads &amp; more</div>
                </div>
              </div>

              {/* Composition deliberately stops at two floating badges + the
                  preview card. Every metric shown is either a live DB value
                  or a clearly-labeled product capability. */}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-primary mb-2">Service categories</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-mv-secondary">Browse by what you actually need</h2>
          </div>
          <Link to="/browse" className="text-sm font-bold text-mv-primary hidden md:inline-flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {cats.slice(0, 12).map((c) => {
            const Icon = ICONS[c.icon] || ChartLineUp;
            return (
              <Link
                key={c.slug}
                to={`/browse?category=${c.slug}`}
                className="mv-card-hover bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-3 group"
                data-testid={`category-card-${c.slug}`}
              >
                <span className="w-10 h-10 rounded-lg bg-mv-primary/10 group-hover:bg-mv-primary group-hover:text-white flex items-center justify-center transition-colors">
                  <Icon size={20} weight="duotone" />
                </span>
                <div>
                  <div className="font-display font-bold text-mv-secondary leading-tight">{c.name}</div>
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">{c.description}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-primary mb-2">Featured services</div>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-mv-secondary">Most-ordered this month</h2>
            </div>
            <Link to="/browse" className="text-sm font-bold text-mv-primary hidden md:inline-flex items-center gap-1 hover:gap-2 transition-all">
              See all services <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((s) => <ServiceCard key={s.id} service={s} />)}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-primary mb-2">Why MarketVyne</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl text-mv-secondary max-w-2xl mx-auto">
            Built to solve what generic marketplaces get wrong.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { i: ShieldCheck, t: "Vetted sellers", d: "Three tiers: New, Verified, Certified Expert. Admin-reviewed identity & track record." },
            { i: Scales, t: "Risk transparency", d: "Every SEO service gets a Low/Medium/High risk label so you know what you're buying." },
            { i: Lock, t: "Buyer protection", d: "Escrow-style holds, structured deliverables, and a fair admin-mediated dispute system." },
          ].map(({i: Icon, t, d}) => (
            <div key={t} className="bg-white rounded-2xl p-7 border border-gray-200">
              <span className="w-12 h-12 rounded-xl bg-mv-primary/10 flex items-center justify-center mb-5">
                <Icon size={26} weight="duotone" className="text-mv-primary" />
              </span>
              <div className="font-display font-bold text-xl text-mv-secondary mb-2">{t}</div>
              <p className="text-sm text-gray-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VERIFIED EXPERTS */}
      {experts.length > 0 && (
        <section className="bg-mv-secondary py-20 text-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-accent mb-2">Certified experts</div>
                <h2 className="font-display font-bold text-3xl lg:text-4xl">Hand-picked specialists you can trust</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {experts.map((e) => (
                <Link key={e.user_id} to={`/seller/${e.user_id}`}
                  className="bg-white/5 hover:bg-white/10 transition-colors rounded-2xl p-5 border border-white/10"
                  data-testid={`featured-expert-${e.user_id}`}>
                  <div className="flex items-center gap-3">
                    <Avatar src={e.user?.avatar_url} name={e.user?.name || ""} size={56} testId={`home-expert-avatar-${e.user?.id || ""}`} />
                    <div>
                      <div className="font-display font-bold">{e.user?.name}</div>
                      <div className="text-xs text-mv-accent uppercase tracking-wider font-bold">{e.tier} expert</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/70 mt-4 line-clamp-2">{e.headline}</p>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-white/60">
                    <div>★ {e.stats?.avg_rating?.toFixed(1)} ({e.stats?.review_count})</div>
                    <div>{e.stats?.completed_orders} orders</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-24">
        <div className="rounded-3xl bg-mv-primary text-white px-8 md:px-14 py-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-mv-accent/30 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display font-black text-3xl md:text-4xl">Ready to grow with experts who deliver?</h2>
            <p className="mt-3 text-white/80">Browse 14 service categories or post your project — get matched in minutes.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="cta-browse">
                <Button className="bg-mv-accent hover:bg-lime-400 text-mv-secondary font-black rounded-xl px-6 py-3 h-auto">
                  Browse services
                </Button>
              </Link>
              <Link to="/register?role=seller" data-testid="cta-seller">
                <Button variant="outline" className="bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-mv-primary rounded-xl px-6 py-3 h-auto font-bold">
                  Start selling
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
